import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx';
import { saveAs } from 'file-saver';

// Função para converter HTML para Document Word
export const convertHtmlToWord = (htmlContent, fileName = 'documento.docx') => {
  try {
    // Parsear HTML simples para extrair conteúdo
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    
    const children = [];
    
    // Função recursiva para processar nós HTML
    const processNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim();
        if (text) {
          return new TextRun(text);
        }
        return null;
      }
      
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();
        const textContent = node.textContent.trim();
        
        if (!textContent) return null;
        
        switch (tagName) {
          case 'h1':
            return new Paragraph({
              children: [new TextRun({ text: textContent, bold: true, size: 32 })],
              heading: HeadingLevel.HEADING_1,
            });
          case 'h2':
            return new Paragraph({
              children: [new TextRun({ text: textContent, bold: true, size: 28 })],
              heading: HeadingLevel.HEADING_2,
            });
          case 'h3':
            return new Paragraph({
              children: [new TextRun({ text: textContent, bold: true, size: 24 })],
              heading: HeadingLevel.HEADING_3,
            });
          case 'h4':
            return new Paragraph({
              children: [new TextRun({ text: textContent, bold: true, size: 22 })],
              heading: HeadingLevel.HEADING_4,
            });
          case 'p': {
            const runs = [];
            for (const child of node.childNodes) {
              if (child.nodeType === Node.TEXT_NODE) {
                const text = child.textContent.trim();
                if (text) {
                  runs.push(new TextRun(text));
                }
              } else if (child.nodeType === Node.ELEMENT_NODE) {
                const childTag = child.tagName.toLowerCase();
                const childText = child.textContent.trim();
                
                if (childText) {
                  switch (childTag) {
                    case 'strong':
                    case 'b':
                      runs.push(new TextRun({ text: childText, bold: true }));
                      break;
                    case 'em':
                    case 'i':
                      runs.push(new TextRun({ text: childText, italics: true }));
                      break;
                    case 'u':
                      runs.push(new TextRun({ text: childText, underline: {} }));
                      break;
                    default:
                      runs.push(new TextRun(childText));
                      break;
                  }
                }
              }
            }
            
            if (runs.length > 0) {
              return new Paragraph({ children: runs });
            } else if (textContent) {
              return new Paragraph({ children: [new TextRun(textContent)] });
            }
            break;
          }
          case 'br':
            return new Paragraph({ children: [new TextRun('')] });
          case 'strong':
          case 'b':
            return new TextRun({ text: textContent, bold: true });
          case 'em':
          case 'i':
            return new TextRun({ text: textContent, italics: true });
          case 'u':
            return new TextRun({ text: textContent, underline: {} });
          default:
            // Para outros elementos, extrair apenas o texto
            if (textContent) {
              return new Paragraph({ children: [new TextRun(textContent)] });
            }
            break;
        }
      }
      
      return null;
    };
    
    // Processar todos os elementos do body
    const bodyElements = doc.body.children;
    for (const element of bodyElements) {
      const paragraph = processNode(element);
      if (paragraph) {
        children.push(paragraph);
      }
    }
    
    // Se não encontrou nenhum elemento estruturado, processar como texto simples
    if (children.length === 0) {
      const plainText = doc.body.textContent.trim();
      if (plainText) {
        // Dividir por quebras de linha e criar parágrafos
        const lines = plainText.split('\n').filter(line => line.trim());
        for (const line of lines) {
          children.push(new Paragraph({ children: [new TextRun(line.trim())] }));
        }
      }
    }
    
    // Se ainda não tem conteúdo, adicionar parágrafo vazio
    if (children.length === 0) {
      children.push(new Paragraph({ children: [new TextRun('Documento vazio')] }));
    }
    
    // Criar documento Word
    const wordDoc = new Document({
      sections: [
        {
          properties: {},
          children: children,
        },
      ],
    });
    
    // Gerar e baixar arquivo
    Packer.toBlob(wordDoc).then((blob) => {
      saveAs(blob, fileName);
    });
    
    return true;
    
  } catch (error) {
    console.error('Erro ao converter para Word:', error);
    return false;
  }
};

// Função para detectar e converter diferentes formatos
export const exportDocumentAsWord = (content, fileName = 'documento', format = 'html') => {
  try {
    let processedContent = content;
    
    // Se for texto simples, converter para HTML básico primeiro
    if (format === 'text') {
      processedContent = content
        .split('\n')
        .map(line => line.trim())
        .filter(line => line)
        .map(line => {
          // Detectar títulos (linhas curtas em maiúscula ou com "---")
          if (line.length < 60 && (line === line.toUpperCase() || line.includes('---'))) {
            return `<h2>${line}</h2>`;
          }
          return `<p>${line}</p>`;
        })
        .join('\n');
    }
    
    const finalFileName = fileName.endsWith('.docx') ? fileName : `${fileName}.docx`;
    return convertHtmlToWord(processedContent, finalFileName);
    
  } catch (error) {
    console.error('Erro ao exportar documento:', error);
    return false;
  }
};

const WordExport = {
  convertHtmlToWord,
  exportDocumentAsWord,
};

export default WordExport;
